/**
 * Entry Point Detection Utility
 *
 * Flow: welcome → signup → howToUse → application → completion
 * 
 * Original complex logic preserved below but disabled:
 * - Email campaign detection (Engaging Networks)
 * - ConsultationInProgress screen flow
 * - Window launcher popup detection
 * 
 * These features will be re-implemented in future architecture revision.
 */

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  marketingConsent: boolean;
}

export interface EntryPoint {
  screen: 'signup' | 'welcome' | 'howToUse' | 'application';
  userData?: UserData;
}

/**
 * Detects which screen the user should start on
 * @returns EntryPoint object with screen and optional user data
 */
export function detectEntryPoint(): EntryPoint {
  const params = new URLSearchParams(window.location.search);

  // PRIORITY 0: Screen parameter from QR code or direct navigation
  const screenParam = params.get('screen');
  if (screenParam && (screenParam === 'signup' || screenParam === 'howToUse' || screenParam === 'welcome' || screenParam === 'application')) {
    console.log(`[detectEntryPoint] Screen parameter detected: ${screenParam}`);
    return { screen: screenParam as EntryPoint['screen'] };
  }

  // PRIORITY 1: Force signup screen via URL parameter (for testing/debugging)
  const forceScreen = params.get('force');
  if (forceScreen === 'signup') {
    console.log('[detectEntryPoint] Force signup parameter detected, starting at signup screen');
    return { screen: 'signup' };
  }

  // Check if progress tracking is disabled via environment variable
  const disableProgressTracking = import.meta.env.VITE_DISABLE_PROGRESS_TRACKING === 'true';
  
  if (disableProgressTracking) {
    console.log('[detectEntryPoint] Progress tracking disabled, starting at welcome screen');
    return { screen: 'welcome' };
  }

  // TODO: Re-engineer entry point logic - temporarily disabled complex detection
  // Original PRIORITY 1: Check for encoded consultation parameter (popup window entry)
  // Original PRIORITY 2: Email campaign entry - EN passes these params
  // Original PRIORITY 3: Check localStorage for returning user
  // 
  // For now, simplified flow:
  // - Check localStorage for returning user (basic support)
  // - Default to welcome screen

  // Simplified: Check localStorage for returning user
  const savedData = localStorage.getItem('consultationUser');
  if (savedData) {
    try {
      const userData = JSON.parse(savedData) as UserData;
      const savedProgress = localStorage.getItem('ciwf-wizard-storage');

      if (savedProgress) {
        console.log('[detectEntryPoint] Returning user with progress, starting at application screen');
        return {
          screen: 'application',
          userData
        };
      }

      // Returning users without progress go to welcome screen
      console.log('[detectEntryPoint] Returning user without progress, starting at welcome screen');
      return { screen: 'welcome' };
    } catch (e) {
      console.error('[detectEntryPoint] Failed to parse saved user data', e);
    }
  }

  // PRIORITY 4: Default - new user entry
  console.log('[detectEntryPoint] No entry point detected, starting at welcome screen');
  return { screen: 'welcome' };
}

/**
 * Clears entry point data from localStorage
 * Call this when user completes the consultation
 */
export function clearEntryPointData(): void {
  localStorage.removeItem('consultationUser');
  localStorage.removeItem('ciwf-wizard-storage');
  localStorage.removeItem('consultationProgress');
}
